import {StreamClient, ImmediateCheckError} from './stream-client.library';
import type {JobReady} from './stream-client.library';

/** Result returned by {@link tryStreamPollingFastPath}. */
export type FastPathResult<R> = {handled: true; result: R} | {handled: false};

/** Factory that produces a {@link StreamClient}. Overridable for tests. */
export type StreamClientFactory = () => StreamClient;

/** Module-singleton client, lazily constructed when the fast path is first used. */
let singletonClient: StreamClient | null = null;

/** Build (or reuse) the module singleton {@link StreamClient}. */
function getOrCreateClient(factory: StreamClientFactory): StreamClient {
  singletonClient ??= factory();
  return singletonClient;
}

/**
 * Default factory: constructs the real {@link StreamClient} using
 * production seams sourced from {@link window.PMW} and browser globals.
 */
const defaultFactory: StreamClientFactory = (): StreamClient =>
  new StreamClient({
    getToken: (): Promise<{token: string; ns: string}> =>
      window.PMW.readLocal('stream/getStreamToken') as Promise<{token: string; ns: string}>,
    eventSourceFactory: (url: string) => new EventSource(url, {withCredentials: true}),
    fetchFn: (...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => window.fetch(...args),
  });

/** Narrow `initialData` to an object with a string `pollingKey`. */
function extractPollingKey(initialData: unknown): string | null {
  if (typeof initialData !== 'object' || initialData === null) {
    return null;
  }
  const record = initialData as Record<string, unknown>;
  const key = record.pollingKey;
  return typeof key === 'string' ? key : null;
}

/** Sentinel symbol used to distinguish a bail result from a real {@link JobReady}. */
const BAIL = Symbol('bail');

/** Why the fast path stopped waiting. `cancel` is a deliberate stop, not a failure. */
type BailReason = 'timeout' | 'cancel';

/**
 * Records an SSE fast-path fallback to the backend client log so SSE health is
 * visible in production (queryable in the `Web` CloudWatch group). Fire-and-forget
 * and never throws — telemetry must not affect the polling flow. Only genuine SSE
 * failures are reported; flag-off / no-pollingKey opt-outs and deliberate cancels
 * are not, so the signal reflects real degradation.
 */
function reportFastPathFallback(reason: 'timeout' | 'error' | 'not-available', key: string, connected?: boolean): void {
  try {
    // key identifies the flow (its suffix, e.g. _image_removebg) and correlates with
    // backend logs; connected (when known) splits a timeout into "gateway never
    // connected" (infra/auth) vs "connected but no event" (publish/delivery/slow job).
    const connectedPart = connected === undefined ? '' : ` connected=${connected}`;
    window.PMW.log(`stream-fastpath-fallback reason=${reason} key=${key}${connectedPart}`);
  } catch {
    // swallow: a telemetry failure must never break the polling fallback
  }
}

/**
 * Attempts to satisfy a polling call via the SSE gateway (fast path) instead
 * of the legacy busy-wait loop.
 *
 * Returns `{handled: true, result}` when the SSE path successfully determined
 * the job is done; returns `{handled: false}` whenever the fast path is
 * unavailable, inapplicable, or encounters any error. The caller must fall
 * back to the legacy polling loop on `{handled: false}`.
 *
 * @param initialData    - The value returned by the initial ajaxFunction call.
 *                         Must carry a string `pollingKey` field to opt in.
 * @param pollingFunction - The caller's polling function; called once after the
 *                         SSE ready event to read the final result.  Envelope
 *                         shape: `{isAvailable: boolean, data: R}`.
 * @param timeout        - Max ms to wait before bailing and falling back to legacy.
 *                         Defaults to 30 minutes (matching the legacy loop default).
 * @param eventId        - When provided, a `polling:stop:<eventId>` document event
 *                         also triggers a bail so the legacy loop's cancellation
 *                         logic takes over.
 * @param clientFactory  - Overridable in tests; defaults to the production seams.
 */
export async function tryStreamPollingFastPath<R>(
  initialData: unknown,
  pollingFunction: (d: unknown, immediate?: boolean) => Promise<{isAvailable: boolean; data: R}>,
  timeout = 1_800_000,
  eventId: string | null = null,
  clientFactory: StreamClientFactory = defaultFactory
): Promise<FastPathResult<R>> {
  // Guard 1: feature flag must be enabled.
  if (!window.PMW.streamGatewayEnabled) {
    return {handled: false};
  }

  // Guard 2: initialData must carry a pollingKey to opt in.
  const pollingKey = extractPollingKey(initialData);
  if (pollingKey === null) {
    return {handled: false};
  }

  try {
    const client = getOrCreateClient(clientFactory);

    /**
     * Immediate check passed to awaitJob: if the result is already available
     * in the backend store (race-free after the initial request), resolve
     * immediately without waiting for the SSE event.
     */
    const immediateCheck = async (): Promise<JobReady | null> => {
      const envelope = await pollingFunction(initialData, true);
      if (envelope.isAvailable) {
        return {key: pollingKey, payload: envelope.data};
      }
      return null;
    };

    // Build a bail sentinel that resolves on timeout or on a stop (cancel) event.
    // Track WHY we bailed: a deliberate cancel must not be logged as an SSE failure.
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let stopListener: (() => void) | null = null;
    let bailReason: BailReason = 'timeout';
    const bailPromise = new Promise<typeof BAIL>((resolve) => {
      timeoutId = setTimeout(() => {
        bailReason = 'timeout';
        resolve(BAIL);
      }, timeout);
      if (eventId !== null) {
        const eventName = `polling:stop:${eventId}`;
        stopListener = (): void => {
          bailReason = 'cancel';
          resolve(BAIL);
        };
        document.addEventListener(eventName, stopListener);
      }
    });

    let ready: JobReady | typeof BAIL;
    try {
      // Wait for the live SSE ready event, the immediate check, or the bail sentinel.
      ready = await Promise.race([client.awaitJob(pollingKey, immediateCheck), bailPromise]);
    } finally {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      if (eventId !== null && stopListener !== null) {
        document.removeEventListener(`polling:stop:${eventId}`, stopListener);
      }
    }

    // Bail: drop the orphaned resolver and fall back to the legacy loop. Report only
    // a timeout (real SSE non-delivery); a cancel is the caller deliberately stopping,
    // which pollingAjaxCallAsync handles without re-entering the legacy loop.
    if (ready === BAIL) {
      client.cancelJob(pollingKey);
      if (bailReason === 'timeout') {
        reportFastPathFallback('timeout', pollingKey, client.isConnected());
      }
      return {handled: false};
    }

    // If the winning arm carried an inlined payload (either from immediateCheck or
    // from a live SSE event with payload attached), return it directly — no second
    // pollingFunction call needed on the common path.
    if (ready.payload !== null && ready.payload !== undefined) {
      return {handled: true, result: ready.payload as R};
    }

    // Rare large-payload case: PHP published payload: null (result too big for the
    // SSE event). Fetch the result once via pollingFunction. A throw here means the
    // JOB itself errored (status read rejected) — NOT an SSE failure: fall back so the
    // legacy loop re-fetches and surfaces it, without logging an SSE fallback.
    const envelope = await pollingFunction(initialData, true).catch(() => null);
    if (envelope === null) {
      return {handled: false};
    }
    if (envelope.isAvailable) {
      return {handled: true, result: envelope.data};
    }

    // SSE fired but the result still isn't in the store (race / spurious event). Fall back.
    reportFastPathFallback('not-available', pollingKey, client.isConnected());
    return {handled: false};
  } catch (e) {
    // An immediate status-read rejection means the JOB errored (the status endpoint
    // returned an error response), NOT that the SSE transport failed. Fall back to the
    // legacy loop — which re-fetches and surfaces the job error to the caller — WITHOUT
    // logging an SSE fallback. Mirrors the null-payload re-read path above. Everything
    // else (token fetch, gateway/EventSource connect, /subscribe) is real SSE degradation.
    if (!(e instanceof ImmediateCheckError)) {
      reportFastPathFallback('error', pollingKey);
    }
    return {handled: false};
  }
}

/** Reset the module singleton. Only for use in unit tests. */
export function resetSingletonForTest(): void {
  singletonClient = null;
}
